// S3: transport-neutral primitives for the free-supplier domain APIs — the
// shared error shape + executor-based parse mechanics, with NO dependency on
// `http` (the authenticated axios instance from helpers/requests.js).
//
// Kept separate from supplierApiCore.js so parsing/error normalization remains
// transport-neutral and directly testable.
//
// supplierApiCore.js re-exports all three of these for its existing
// importers (supplierFactorApi.js, supplierDraftApi.js, supplierUploadApi.js,
// supplierInboxApi.js, supplierPublicationApi.js, supplierDiagramApi.js) —
// none of those import sites needed to change.

export class SupplierApiError extends Error {
  constructor(
    payload,
    httpStatus,
    defaultMessage = 'Supplier request failed',
    resolveMessage = (p) => p?.message,
  ) {
    super(resolveMessage(payload) || defaultMessage);
    this.name = 'SupplierApiError';
    this.payload = payload;
    this.httpStatus = httpStatus;
    // Shaped like an axios error so notifyError / callers read .response.data.
    this.response = { status: httpStatus, data: payload };
  }
}

// error?.response?.data present + an object → throw an ErrorClass instance
// shaped from it (a SupplierApiError subclass); otherwise rethrow the
// original error untouched (network failure, etc — nothing to map).
export const mapSupplierApiError = (error, ErrorClass) => {
  if (error?.response?.data && typeof error.response.data === 'object') {
    throw new ErrorClass(error.response.data, error.response.status);
  }
  throw error;
};

// The one primitive every domain's get/post/patch-and-parse helper reduces
// to: run an executor (the actual request, however its payload is built —
// JSON body, multipart FormData, an authenticated or a public GET, no body
// at all), then parse the response through the domain's schema, mapping any
// failure through the domain's error class. Parameterizing on the EXECUTOR
// rather than the HTTP method/transport means neither a new verb nor a
// different transport (authenticated vs public) needs its own near-duplicate
// wrapper — only the executor closure differs.
export const supplierRequestAndParse = async (executor, schema, mapApiError) => {
  try {
    const response = await executor();
    return schema.parse(response?.data);
  } catch (error) {
    return mapApiError(error);
  }
};
